<template>
  <div
    class="d-flex flex-column bg-white border border-outline-color p-3 round-16"
  >
    <div class="d-flex align-items-center">
      <div class="avatar rounded-circle">
        <img v-if="user.avatar" :src="user.avatar" class="avatar" />
        <DefaultAvatar v-else class="avatar" />
      </div>
      <div class="flex-grow-1">
        <p class="text-weight-500">{{ user.fullName }}</p>
        <div class="d-flex align-items-center mt-1">
          <RankIcon14 :rank="user.rank" />
          <p class="text-gray-05 text-size-12 ml-1">
            {{ currentRankLabel }}
          </p>
        </div>
      </div>
    </div>

    <div class="d-flex">
      <div class="left-blank"></div>
      <div class="flex-grow-1">
        <RouterLink
          :to="{ name: 'MyReview' }"
          class="d-flex justify-content-between align-items-center bg-gray-01 p-2 mt-2 round-8"
        >
          <div class="d-flex align-items-center">
            <FiveStars
              :filled-star-image-src="filledStarImageSrc"
              :filled-star-half-image-src="filledStarHalfImageSrc"
              :value="truncatedAverageEvaluation"
            />
            <p class="text-size-12 ml-2">
              {{ truncatedAverageEvaluation }}点 ({{
                avgEvaluation.count || 0
              }}件)
            </p>
          </div>
          <FeatherIcon icon="chevron-right" size="1x" class="text-gray-05" />
        </RouterLink>
        <div class="d-flex bg-gray-01 p-2 mt-2 round-8">
          <div class="pr-3">
            <p class="text-gray-05 text-size-10 pr-4">勤務回数</p>
            <div class="text-gray-05 text-size-10 pr-2">
              <span class="text-size-16 text-weight-600 ml-1 mr-1">{{
                user.workCount
              }}</span
              >回
            </div>
            <p class="text-weight-600 pr-4"></p>
          </div>
          <div>
            <p class="text-gray-05 text-size-10">累計給与額</p>
            <p class="text-weight-600">
              ¥ {{ balance.totalIncome.toLocaleString() }}
            </p>
          </div>
        </div>
        <RouterLink
          :to="{ name: 'CancelRate' }"
          class="d-flex align-items-center bg-gray-01 p-2 mt-2 round-8"
        >
          <div class="pr-3">
            <div class="pr-2">
              <p class="text-gray-05 text-size-10 text-nowrap">
                キャンセル率
              </p>
              <span>
                <CancelPercentage
                  :percentage="cancelPercentage"
                  numberClass="text-size-16 text-weight-600 ml-1"
                  unitClass="text-size-10"
                />
              </span>
            </div>
            <p class="text-weight-600 pr-4"></p>
          </div>
          <div class="pr-3">
            <div class="pr-2">
              <p class="text-gray-05 text-size-10 text-nowrap">
                直前キャンセル率
              </p>
              <CancelPercentageSudden
                :percentage="suddenCancelPercentage"
                numberClass="text-size-16 text-weight-600 ml-1"
                unitClass="text-size-10"
              />
            </div>
            <p class="text-weight-600 pr-4"></p>
          </div>
          <FeatherIcon
            icon="chevron-right"
            size="1x"
            class="ml-auto text-gray-05"
          />
        </RouterLink>
        <div class="bg-gray-01 p-2 mt-2 round-8">
          <p class="text-gray-05 text-size-10 pr-4">
            <a class="link text-blue" :href="availableApplicationCountFaqUrl"
              >応募可能な件数</a
            >
          </p>
          <div class="d-flex pr-2">
            <div class="text-gray-05 text-size-10 pr-2">
              あと<span class="text-size-16 text-weight-600 ml-1 mr-1">{{
                myApplicationCount.available
              }}</span
              >件
            </div>
            <div class="text-gray-05 text-size-10 pr-2">
              （同時応募は最大<span
                class="text-size-16 text-weight-600 ml-1 mr-1"
                >{{ myApplicationCount.limit }}</span
              >
              件まで）
            </div>
          </div>
          <div
            v-if="needDisplayCreditScoreRecoveryDate"
            data-testid="creditScoreRecoveryDate"
          >
            <div class="text-danger text-size-10 pr-2">
              信用スコアが<span
                class="text-danger text-size-16 text-weight-600 ml-1 mr-1"
                >0</span
              >pt
              のため、限定公開・指名オファー以外のお仕事には一時的に応募できません
            </div>
            <div class="text-danger text-size-10 pr-2">
              <span class="text-danger text-size-16 text-weight-600 mr-1">{{
                displayCreditScoreRecoveryDate
              }}</span
              >に信用スコアが回復し、制限が解除されます
            </div>
          </div>
          <div v-else>
            <div class="text-danger text-size-10 pr-2">
              当日・翌日のお仕事についてはこの件数に関係なく応募可能です
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
// @ts-ignore
import DefaultAvatar from "@/assets/svg/DefaultAvatar.svg?component";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import RankIcon14 from "@/components/molecules/other/RankIcon14.vue";
import FiveStars from "@/components/molecules/other/FiveStars.vue";
import { evaluationRound } from "@/utils/evaluationRound";
import { User } from "@/types/user/User";
import { TotalAverageEvaluationByOffice } from "@/types/worker/TotalAverageEvaluationByOffice";
import { AvailableCount } from "@/types/order/AvailableCount";
import { Balance } from "@/types/balance/Balance";
import CancelPercentage from "@/components/molecules/other/CancelPercentage.vue";
import CancelPercentageSudden from "@/components/molecules/other/CancelPercentageSudden.vue";
import { getCreditScoreRecoveryDateForDisplay } from "@/utils/creditScore";

@Component({
  components: {
    FiveStars,
    FeatherIcon,
    DefaultAvatar,
    RankIcon14,
    CancelPercentage,
    CancelPercentageSudden
  }
})
export default class ProfileCard extends Vue {
  @Prop({
    type: Object,
    required: true
  })
  user!: User;

  @Prop({
    type: String,
    required: true
  })
  currentRankLabel!: string;

  @Prop({
    type: Object,
    required: true
  })
  avgEvaluation!: TotalAverageEvaluationByOffice;

  @Prop({
    type: Object,
    required: true
  })
  balance!: Balance;

  @Prop({
    type: Object,
    required: true
  })
  myApplicationCount!: AvailableCount;

  @Prop({
    type: Number,
    required: true
  })
  cancelPercentage!: number;

  @Prop({
    type: Number,
    required: true
  })
  suddenCancelPercentage!: number;

  filledStarImageSrc = require("@/assets/svg/star/filled_star_yellow.svg");
  filledStarHalfImageSrc = require("@/assets/svg/star/filled_star_half_yellow.svg");
  availableApplicationCountFaqUrl =
    process.env.VUE_APP_AVAILABLE_APPLICATION_COUNT_FAQ_URL;

  get truncatedAverageEvaluation() {
    return evaluationRound(this.avgEvaluation.totalAverage);
  }

  get displayCreditScoreRecoveryDate() {
    return getCreditScoreRecoveryDateForDisplay(this.user);
  }

  get needDisplayCreditScoreRecoveryDate() {
    if (this.user.creditScore > 0) {
      return false;
    }
    return this.displayCreditScoreRecoveryDate !== null;
  }
}
</script>

<style lang="scss" scoped>
.avatar {
  width: 48px;
  height: 48px;
  margin-right: 10px;
  overflow: hidden;
  object-fit: cover;
}

.left-blank {
  flex: 0 0 48px;
  width: 48px;
  margin-right: 10px;
}

.link {
  text-decoration: underline;
}
</style>
